import { config } from '../config/environment';
import { getAccessToken } from './reports';

const ANALYTICS_SERVICE_URL = config.analyticsServiceBase || 'http://localhost:7071/api';
const ANALYTICS_FUNCTIONS_KEY = process.env.REACT_APP_ANALYTICS_API_KEY || 'qmmhZL8BdaqcJqV1_pADAXEVi-V-37li8iVbV8_3pTuzAzFuq-vNUA==';

class AnalyticsService {
  constructor() {
    this.baseUrl = ANALYTICS_SERVICE_URL;
  }

  async query(query, variables = {}) {
    try {
      const token = await getAccessToken();
      
      const response = await fetch(`${this.baseUrl}/analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-functions-key': ANALYTICS_FUNCTIONS_KEY,
          'x-user-id': this.getUserId(),
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      return result.data;
    } catch (error) {
      console.error('GraphQL query error:', error);
      throw error;
    }
  }

  getUserId() {
    // Get user ID from MSAL or other auth context
    // This should match how you get the user ID in your app
    const account = JSON.parse(localStorage.getItem('msalAccount') || '{}');
    return account.localAccountId || 'anonymous';
  }

  // Dashboard configuration methods
  async getDashboardConfig(userId) {
    const query = `
      query GetDashboardConfig($userId: String!) {
        dashboardConfig(userId: $userId) {
          userId
          widgets {
            id
            type
            title
            description
            config
            position {
              x
              y
              width
              height
            }
            isVisible
            order
          }
          layout
          updatedAt
        }
      }
    `;

    const result = await this.query(query, { userId });
    return result.dashboardConfig;
  }

  async updateDashboardConfig(userId, widgets, layout) {
    const mutation = `
      mutation UpdateDashboardConfig($userId: String!, $widgets: [DashboardWidgetInput], $layout: String) {
        updateDashboardConfig(userId: $userId, widgets: $widgets, layout: $layout) {
          userId
          widgets {
            id
            type
            title
            description
            config
            position {
              x
              y
              width
              height
            }
            isVisible
            order
          }
          layout
          updatedAt
        }
      }
    `;

    const result = await this.query(mutation, { userId, widgets, layout });
    return result.updateDashboardConfig;
  }

  async addWidget(userId, type, title, config = {}, position = null) {
    const mutation = `
      mutation AddWidget($userId: String!, $type: String!, $title: String, $config: JSON, $position: WidgetPositionInput) {
        addWidget(userId: $userId, type: $type, title: $title, config: $config, position: $position) {
          id
          type
          title
          description
          config
          position {
            x
            y
            width
            height
          }
          isVisible
          order
        }
      }
    `;

    const result = await this.query(mutation, { 
      userId, 
      type, 
      title, 
      config, 
      position 
    });
    return result.addWidget;
  }

  async removeWidget(userId, widgetId) {
    const mutation = `
      mutation RemoveWidget($userId: String!, $widgetId: String!) {
        removeWidget(userId: $userId, widgetId: $widgetId)
      }
    `;

    const result = await this.query(mutation, { userId, widgetId });
    return result.removeWidget;
  }

  async updateWidgetPosition(userId, widgetId, position) {
    const mutation = `
      mutation UpdateWidgetPosition($userId: String!, $widgetId: String!, $position: WidgetPositionInput!) {
        updateWidgetPosition(userId: $userId, widgetId: $widgetId, position: $position) {
          id
          type
          title
          description
          config
          position {
            x
            y
            width
            height
          }
          isVisible
          order
        }
      }
    `;

    const result = await this.query(mutation, { userId, widgetId, position });
    return result.updateWidgetPosition;
  }

  // Analytics data methods
  async getAnalyticsMetrics(userId, dateRange = null) {
    const query = `
      query GetAnalyticsMetrics($userId: String, $dateRange: DateRangeInput) {
        analyticsMetrics(userId: $userId, dateRange: $dateRange) {
          totalCanvasses
          totalInterviews
          totalCompanies
          totalStakeholders
          averageCompletionRate
          activeCanvasses
          dateRange {
            start
            end
          }
        }
      }
    `;

    const result = await this.query(query, { userId, dateRange });
    return result.analyticsMetrics;
  }

  async getTopEmotions(userId, canvassIds = null, limit = 10) {
    const query = `
      query GetTopEmotions($userId: String, $canvassIds: [String], $limit: Int) {
        topEmotions(userId: $userId, canvassIds: $canvassIds, limit: $limit) {
          emotion
          frequency
          averageStrength
          totalOccurrences
        }
      }
    `;

    const result = await this.query(query, { userId, canvassIds, limit });
    return result.topEmotions;
  }

  async getCompletionRates(userId, canvassIds = null) {
    const query = `
      query GetCompletionRates($userId: String, $canvassIds: [String]) {
        completionRates(userId: $userId, canvassIds: $canvassIds) {
          canvassId
          canvassName
          rate
          interviewCount
        }
      }
    `;

    const result = await this.query(query, { userId, canvassIds });
    return result.completionRates;
  }

  // Data fetching methods for dashboard
  async getCanvasses(userId, options = {}) {
    const query = `
      query GetCanvasses($userId: String, $companyId: String, $stakeholderId: String, $status: String, $environment: String, $dateRange: DateRangeInput, $limit: Int, $offset: Int) {
        canvasses(userId: $userId, companyId: $companyId, stakeholderId: $stakeholderId, status: $status, environment: $environment, dateRange: $dateRange, limit: $limit, offset: $offset) {
          id
          canvassId
          canvassName
          objective
          status
          environment
          audience
          createdAt
          updatedAt
          interviewCount
          completionRate
          metadata
        }
      }
    `;

    const result = await this.query(query, { userId, ...options });
    return result.canvasses;
  }

  async getInterviews(userId, options = {}) {
    const query = `
      query GetInterviews($userId: String, $canvassId: String, $status: String, $dateRange: DateRangeInput, $limit: Int, $offset: Int) {
        interviews(userId: $userId, canvassId: $canvassId, status: $status, dateRange: $dateRange, limit: $limit, offset: $offset) {
          id
          sessionId
          canvassId
          participantId
          status
          startedAt
          completedAt
          messageCount
          duration
          sentiment {
            averageSentimentScore
            topEmotions {
              emotion
              frequency
              averageStrength
              totalOccurrences
            }
          }
          insights {
            psychographicArchetype
            dominantEmotion
            keyThemes
            recommendations
            evaluatedAt
          }
        }
      }
    `;

    const result = await this.query(query, { userId, ...options });
    return result.interviews;
  }

  async getCompanies(userId, options = {}) {
    const query = `
      query GetCompanies($userId: String, $isPublic: Boolean, $limit: Int, $offset: Int) {
        companies(userId: $userId, isPublic: $isPublic, limit: $limit, offset: $offset) {
          id
          name
          url
          industry
          objective
          isPublic
          createdAt
          updatedAt
          employeeCount
          metadata
        }
      }
    `;

    const result = await this.query(query, { userId, ...options });
    return result.companies;
  }

  async getStakeholders(userId, options = {}) {
    const query = `
      query GetStakeholders($userId: String, $parentCompanyId: String, $isInternal: Boolean, $isPublic: Boolean, $limit: Int, $offset: Int) {
        stakeholders(userId: $userId, parentCompanyId: $parentCompanyId, isInternal: $isInternal, isPublic: $isPublic, limit: $limit, offset: $offset) {
          id
          name
          parentCompanyId
          objective
          isInternal
          isPublic
          createdAt
          updatedAt
          metadata
          parentCompany {
            id
            name
            industry
          }
        }
      }
    `;

    const result = await this.query(query, { userId, ...options });
    return result.stakeholders;
  }

  // Canvass-specific analytics endpoint
  async getCanvassAnalytics(canvassId) {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${this.baseUrl}/analytics/canvass-analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-functions-key': ANALYTICS_FUNCTIONS_KEY,
        },
        body: JSON.stringify({ canvassId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Canvass analytics error:', error);
      throw error;
    }
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;
